/**
 * Ticket LM1: Dankeseite eines Freebies mit dem eigentlichen Download.
 *
 * Das Kürzel der Dankeseite ist je Freebie frei wählbar, damit Anzeigen und
 * Auswertungen auf eine eigene Adresse zeigen können. Der Zugang steckt in der
 * Adresse (`?zugang=`) und gilt sieben Tage; der Downloadlink selbst nur 15 Minuten.
 */

import { useEffect, useState } from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Layout } from "@/components/Layout";
import { getLeadMagnetDelivery, getLeadMagnetPage } from "@/lib/lead-magnets.functions";
import { leadMagnetPath, type DeliveryOutcome } from "@/lib/lead-magnets";
import { TOOL_ACCESS_MESSAGES, type ToolAccessPlan } from "@/lib/lead-magnet-access";

/**
 * `zugang` = Downloadkennung bei Datei-Freebies.
 * `zugriff` = Ausgang der Kontoanlage bei Werkzeug-Freebies (Ticket LM3).
 */
type Search = { zugang?: string; zugriff?: ToolAccessPlan["outcome"] };

const TOOL_OUTCOMES: ToolAccessPlan["outcome"][] = ["created", "granted", "already"];

export const Route = createFileRoute("/kostenlos/$slug/$step")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    zugang: typeof search.zugang === "string" ? search.zugang : undefined,
    zugriff: TOOL_OUTCOMES.includes(search.zugriff as ToolAccessPlan["outcome"])
      ? (search.zugriff as ToolAccessPlan["outcome"])
      : undefined,
  }),
  loader: async ({ params }) => {
    const magnet = await getLeadMagnetPage({ data: { slug: params.slug } });
    if (!magnet || magnet.thanksSlug !== params.step) throw notFound();
    return { magnet };
  },
  head: ({ loaderData }) => {
    const title = loaderData
      ? `Ihr Download steht bereit | ${loaderData.magnet.title}`
      : "Nicht verfügbar | creategy";
    return {
      meta: [
        { title },
        { name: "robots", content: "noindex, nofollow" },
        {
          name: "description",
          content: "Ihr Download steht bereit. Der Zugang gilt sieben Tage.",
        },
        { property: "og:title", content: title },
        {
          property: "og:description",
          content: "Ihr Download steht bereit. Der Zugang gilt sieben Tage.",
        },
        { property: "og:type", content: "website" },
      ],
    };
  },
  component: LeadMagnetThanksPage,
});

function LeadMagnetThanksPage() {
  const { magnet } = Route.useLoaderData();
  const { zugang, zugriff } = Route.useSearch();
  const isTool = magnet.kind === "werkzeug";
  const fetchDelivery = useServerFn(getLeadMagnetDelivery);
  const [state, setState] = useState<DeliveryOutcome | { status: "loading" }>(
    zugang ? { status: "loading" } : { status: "unknown" },
  );

  useEffect(() => {
    if (!zugang || isTool) return;
    let active = true;
    fetchDelivery({ data: { slug: magnet.slug, token: zugang } })
      .then((result) => {
        if (active) setState(result);
      })
      .catch(() => {
        if (active) setState({ status: "unknown" });
      });
    return () => {
      active = false;
    };
  }, [zugang, isTool, magnet.slug, fetchDelivery]);

  return (
    <Layout>
      <section>
        <div className="section-y mx-auto w-full max-w-[900px] px-6 md:px-12">
          <h1 className="heading-h1 text-primary">Ihr Download steht bereit.</h1>

          <p className="mt-8 font-display text-xl font-extrabold text-primary">
            {magnet.title}
          </p>
          <p className="mt-4 font-body text-base leading-relaxed text-foreground md:text-lg">
            Wir haben Ihren Zugang zusätzlich per E-Mail geschickt. So kommen Sie
            in den nächsten sieben Tagen weiterhin an Ihre Datei.
          </p>


          {isTool ? (
            <div className="mt-10 rounded-sm border border-foreground/15 p-6 md:p-8">
              <h2 className="font-display text-xl font-extrabold text-primary">
                Ihr Zugang zum Cockpit
              </h2>
              <p className="mt-4 font-body text-base leading-relaxed text-foreground">
                {TOOL_ACCESS_MESSAGES[zugriff ?? "created"]}
              </p>
              <Link
                to="/cockpit"
                className="mt-6 inline-flex items-center justify-center rounded-sm bg-brand-button px-6 py-3 font-display text-base font-extrabold text-brand-button-foreground transition-opacity hover:opacity-90"
              >
                Zum Cockpit
              </Link>
            </div>
          ) : (
          <div className="mt-10 rounded-sm border border-foreground/15 p-6 md:p-8">
            {state.status === "loading" && (
              <p className="font-body text-base text-muted-foreground">Zugang wird geprüft …</p>
            )}

            {state.status === "ok" && (
              <div
                className={
                  magnet.previewImageUrl
                    ? "grid items-center gap-8 md:grid-cols-[200px_1fr]"
                    : ""
                }
              >
                {magnet.previewImageUrl && (
                  <div className="flex items-center justify-center rounded-sm bg-white/50 p-4">
                    <img
                      src={magnet.previewImageUrl}
                      alt={magnet.title}
                      width={200}
                      height={160}
                      className="h-auto max-h-48 w-auto object-contain"
                    />
                  </div>
                )}
                <div>
                  <h2 className="font-display text-xl font-extrabold text-primary">
                    {magnet.title}
                  </h2>
                  <a
                    href={state.downloadUrl}
                    className="mt-6 inline-flex items-center justify-center rounded-sm bg-brand-button px-6 py-3 font-display text-base font-extrabold text-brand-button-foreground transition-opacity hover:opacity-90"
                  >
                    Datei herunterladen
                  </a>
                  <p className="mt-4 font-body text-sm text-muted-foreground">
                    Der Downloadlink ist aus Sicherheitsgründen 15 Minuten gültig. Laden Sie
                    diese Seite über den Link aus Ihrer E-Mail neu, um einen neuen zu erhalten.
                  </p>
                </div>
              </div>
            )}


            {state.status === "expired" && (
              <>
                <h2 className="font-display text-xl font-extrabold text-primary">
                  Der Zugang ist abgelaufen
                </h2>
                <p className="mt-4 font-body text-base leading-relaxed text-foreground">
                  Zugänge gelten sieben Tage. Fordern Sie die Unterlage einfach erneut an —
                  das dauert einen Moment.
                </p>
                <Link
                  to="/kostenlos/$slug"
                  params={{ slug: magnet.slug }}
                  className="mt-6 inline-flex items-center justify-center rounded-sm bg-brand-button px-6 py-3 font-display text-base font-extrabold text-brand-button-foreground transition-opacity hover:opacity-90"
                >
                  Erneut anfordern
                </Link>
              </>
            )}

            {state.status === "unknown" && (
              <>
                <h2 className="font-display text-xl font-extrabold text-primary">
                  Bitte nutzen Sie den Link aus Ihrer E-Mail
                </h2>
                <p className="mt-4 font-body text-base leading-relaxed text-foreground">
                  Der Download öffnet sich über den persönlichen Zugang aus der zweiten
                  E-Mail. Haben Sie sie nicht erhalten, fordern Sie die Unterlage erneut an.
                </p>
                <p className="mt-6 font-body text-sm text-muted-foreground">
                  <Link to={leadMagnetPath(magnet.slug)} className="underline">
                    Zurück zum Formular
                  </Link>
                </p>
              </>
            )}
          </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
